import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  GridApiPremium,
  GridRowSelectionModel,
  GridColumnVisibilityModel,
  GridRowModel,
} from '@mui/x-data-grid-premium';
import Actions from '../../store/Actions';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { OTCDatabaseRow } from './types';
import { useGroups } from '../../hooks/useGroups';
import { useSyncOperations } from '../../hooks/useSyncOperations';
import { IllioOTCDatabaseWithLinksResponse } from '../../types/generatedTypes';

interface UseOTCDatabaseDataProps {
  apiRef: React.RefObject<GridApiPremium | null>;
  displayData: OTCDatabaseRow[];
}

export const useOTCDatabaseData = ({
  apiRef,
  displayData,
}: UseOTCDatabaseDataProps) => {
  const [otcData, setOtcData] = useState<OTCDatabaseRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [autosizeColumns, setAutosizeColumns] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      asset_sub_class: false,
    });

  // Track if we've attempted auto-sync on empty (to prevent infinite loop)
  const hasAttemptedAutoSync = useRef(false);

  // Sync operations
  const { isSyncingOTCDatabase, syncOTCDatabaseFromIllio } =
    useSyncOperations();

  // Groups functionality
  const {
    groups,
    setGroups,
    isGroupingEnabled,
    setIsGroupingEnabled,
    isGroupsModalOpen,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = useGroups({ pageType: 'otc_database' });

  // Column fields configuration for autosize
  const columnFields = useMemo(
    () => [
      'instrument_status',
      'name',
      'code',
      'country',
      'currency',
      'multiplier',
      'asset_sub_class',
    ],
    []
  );

  // Handle autosize state changes
  useEffect(() => {
    if (autosizeColumns && apiRef.current) {
      apiRef.current.autosizeColumns({
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
        columns: columnFields,
      });
    } else if (!autosizeColumns && apiRef.current) {
      // Reset column widths to allow flex to work
      columnFields.forEach(field => {
        apiRef.current?.setColumnWidth(field, 0);
      });
    }
  }, [autosizeColumns, apiRef, columnFields]);

  // Track dynamic columns extracted from wizard_column data
  const [dynamicColumns, setDynamicColumns] = useState<string[]>([]);

  const fetchOTCDatabaseData = useCallback(async (): Promise<
    OTCDatabaseRow[]
  > => {
    try {
      setLoading(true);
      // Use endpoint - reads from DB (no auto-sync from Illio)
      const response = await Actions.getOTCDatabaseWithLinks();

      // Validate API response structure - no silent fallbacks
      if (!response.data) {
        throw new Error(
          "API response missing 'data' field - check backend endpoint /api/otc_database"
        );
      }

      // Use dynamic_columns from backend response
      const extractedColumns = response.dynamic_columns ?? [];
      setDynamicColumns(extractedColumns);

      // Transform to OTCDatabaseRow with flattened wizard columns
      const transformedData = response.data.map(
        (row: IllioOTCDatabaseWithLinksResponse) => {
          const flatRow: OTCDatabaseRow = { ...row };

          // Flatten wizard_column: for each column name, join values with comma
          extractedColumns.forEach(colName => {
            const values: string[] = [];
            if (row.wizard_column && Array.isArray(row.wizard_column)) {
              row.wizard_column.forEach(entry => {
                if (entry[colName] !== undefined && entry[colName] !== null) {
                  values.push(String(entry[colName]));
                }
              });
            }
            flatRow[`wizard_${colName}`] =
              values.length > 0 ? values.join(', ') : '';
          });

          return flatRow;
        }
      );

      setOtcData(transformedData);
      return transformedData;
    } catch (error) {
      console.error('Failed to fetch OTC database data:', error);
      showSnackbar({
        title: 'Error',
        message: 'Failed to load OTC database data',
        type: SnackbarType.error,
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync handler that syncs from Illio and then refreshes data
  const handleSyncOTCDatabase = useCallback(async () => {
    await syncOTCDatabaseFromIllio(fetchOTCDatabaseData);
  }, [syncOTCDatabaseFromIllio, fetchOTCDatabaseData]);

  // Auto-sync when data is empty on initial load
  useEffect(() => {
    const initializeData = async () => {
      const data = await fetchOTCDatabaseData();
      // If collection is empty and we haven't tried to auto-sync yet, sync once
      if (data.length === 0 && !hasAttemptedAutoSync.current) {
        hasAttemptedAutoSync.current = true;
        handleSyncOTCDatabase();
      }
    };
    initializeData();
  }, [fetchOTCDatabaseData, handleSyncOTCDatabase]);

  // Export data handler
  const handleExport = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv();
    }
  }, [apiRef]);

  // Row selection handler
  const handleRowSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      if (
        !newSelection ||
        typeof newSelection !== 'object' ||
        !('ids' in newSelection)
      ) {
        setSelectedRows([]);
        return;
      }
      const { ids, type } = newSelection;
      if (!(ids instanceof Set)) {
        setSelectedRows([]);
        return;
      }
      const idsArray = Array.from(ids).map(String);
      if (type === 'include') {
        setSelectedRows(idsArray);
      } else if (type === 'exclude') {
        const excludedSet = new Set(idsArray);
        setSelectedRows(
          displayData
            .map(r => r.id.toString())
            .filter(id => !excludedSet.has(id))
        );
      } else {
        setSelectedRows([]);
      }
    },
    [displayData]
  );

  // Prepare available rows for groups modal
  const availableRowsForGroups = useMemo(() => {
    return otcData.map(row => ({
      ...row,
      id: row.id,
      _id: row.id,
      label: `${row.code || 'Unknown'} - ${row.name || 'No Name'}`,
    }));
  }, [otcData]);

  // Process row update for editable cells
  const processRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow?: GridRowModel
    ): Promise<OTCDatabaseRow> => {
      try {
        if (oldRow) {
          const hasChanges = Object.keys(newRow).some(key => {
            if (key === 'id') return false;
            return newRow[key] !== oldRow[key];
          });
          if (!hasChanges) {
            return newRow as OTCDatabaseRow;
          }
        }

        const updatePayload = { multiplier: newRow.multiplier };
        const response = await Actions.updateOTCDatabaseRecord(
          newRow.id,
          updatePayload
        );

        const updatedRow = { ...newRow, ...response.otc } as OTCDatabaseRow;
        setOtcData(prev =>
          prev.map(otc => (otc.id === newRow.id ? updatedRow : otc))
        );

        return updatedRow;
      } catch (error) {
        console.error('Error updating OTC record:', error);
        showSnackbar({
          title: 'Error',
          type: SnackbarType.error,
          message: 'Failed to update OTC record',
        });
        throw error;
      }
    },
    []
  );

  const handleProcessRowUpdateError = useCallback((error: Error) => {
    console.error('Row update error:', error);
    showSnackbar({
      title: 'Update failed',
      type: SnackbarType.error,
      message: error.message || 'Failed to update OTC record',
    });
  }, []);

  return {
    otcData,
    loading,
    selectedRows,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnFields,
    fetchOTCDatabaseData,
    handleExport,
    handleRowSelectionChange,
    // Sync
    isSyncingOTCDatabase,
    handleSyncOTCDatabase,
    // Groups
    groups,
    setGroups,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
    // Row update
    processRowUpdate,
    handleProcessRowUpdateError,
    // Dynamic columns from wizard
    dynamicColumns,
  };
};
